import { BASE_OCPI_ADMIN_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { cleanSetupErrorMessage } from '../utils/error-messages';
import {
  ProtocolConnection,
  OcpiPartyDto,
  PublishLocationsResponse,
  PublishTariffResponse,
  ExternalCpoConnectionRequestDto,
  VpostConnectionRequestDto,
  CpoOcpiOverview,
  CpoSyncOutcome,
  CpoSyncOutcomeResponse,
} from '../types/api-types';

/**
 * OCPI Admin API client
 * Unified API for OCPI connection management, replacing separate ocpi-cpo and kiosk APIs
 * Handles authenticated requests to the OCPI-Admin service endpoints
 */
export class OcpiAdminApi {
  constructor(
    private readonly baseUrl: string = BASE_OCPI_ADMIN_API,
    private readonly tokenManager: TokenManager
  ) {}

  /**
   * Get OCPI overview for an operator
   * GET /ocpi-admin/ocpi-cpo/{nayaxOperatorId}/ocpi-overview
   *
   * Returns operator's OCPI configuration including:
   * - OCPI party identification (partyId, countryCode)
   * - CSMS type (NAYAX_ENERGY = internal, EXTERNAL = external)
   * - All protocol connections
   *
   * @param operatorId - Nayax operator ID
   * @returns CpoOcpiOverview with configuration and connections
   * @throws 404 if operator is not configured for OCPI (needs onboarding)
   */
  async getOcpiOverview(operatorId: number): Promise<CpoOcpiOverview> {
    const endpoint = `/ocpi-cpo/${operatorId}/ocpi-overview`;
    // Use custom error handling to capture full response body for support
    return this.authenticatedRequestWithDetailedError<CpoOcpiOverview>(endpoint, {}, [404]);
  }

  /**
   * Get all connections for an internal CPO operator
   * GET /ocpi-admin/internal-ocpi-cpo/{nayaxOperatorId}/connections
   *
   * @deprecated Use getOcpiOverview() instead - returns connections along with CSMS type
   * @param operatorId - Nayax operator ID
   * @returns Array of protocol connections (OCPI, Hubject, Gireve)
   */
  async getConnections(operatorId: number): Promise<ProtocolConnection[]> {
    const endpoint = `/internal-ocpi-cpo/${operatorId}/connections`;
    // Suppress 404 toast - this is expected when operator is not configured for OCPI
    return this.authenticatedRequest<ProtocolConnection[]>(endpoint, {}, [404]);
  }

  /**
   * Setup or update Kiosk connection for an internal CPO operator
   * PUT /ocpi-admin/internal-ocpi-cpo/{nayaxOperatorId}/connections/nayax-kiosk
   *
   * @param operatorId - Nayax operator ID
   * @throws Error with user-friendly message for common error codes
   */
  async setupKioskConnection(operatorId: number): Promise<void> {
    const endpoint = `/internal-ocpi-cpo/${operatorId}/connections/nayax-kiosk`;
    const headers = await this.tokenManager.getAuthHeaders();
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        accept: '*/*',
        ...headers,
      },
    });

    if (!response.ok) {
      // Handle 409 Conflict - Kiosk connection already exists
      if (response.status === 409) {
        throw new Error(
          'A Kiosk connection already exists for this operator. ' +
          'The connection may need to be updated or removed first.'
        );
      }

      // Handle 404 - Operator not configured
      if (response.status === 404) {
        throw new Error(
          'Operator is not configured for OCPI. Please complete the onboarding process first.'
        );
      }

      // For other errors, use the standard error handling
      await ApiErrorMiddleware.handleErrorResponse(
        response,
        'OCPI-Admin API',
        endpoint
      );

      throw new Error(`OCPI-Admin API request failed with status ${response.status}`);
    }
  }

  /**
   * Setup external CPO connection for an operator
   * POST /ocpi-admin/external-ocpi-cpo/{nayaxOperatorId}/connections/nayax-kiosk/full-setup
   *
   * @param operatorId - Nayax operator ID
   * @param data - External CPO connection configuration (versions_url, token_a, connection_version, csms_name)
   * @throws Error with user-friendly message for common error codes
   */
  async setupExternalCpoConnection(
    operatorId: number,
    data: ExternalCpoConnectionRequestDto
  ): Promise<void> {
    const endpoint = `/external-ocpi-cpo/${operatorId}/connections/nayax-kiosk/full-setup`;
    const headers = await this.tokenManager.getAuthHeaders();
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        accept: '*/*',
        ...headers,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      // Handle 409 Conflict - Connection already exists or configuration conflict
      if (response.status === 409) {
        throw new Error(
          'A connection configuration already exists for this operator. ' +
          'Please contact support if you need to update the existing configuration.'
        );
      }

      // Handle 400 Bad Request - Invalid configuration
      if (response.status === 400) {
        throw new Error(
          'Invalid connection configuration. Please verify the Versions URL, Token, and CSMS name are correct.'
        );
      }

      // For other errors: try to extract 'detail' from the JSON body for a user-friendly message
      let errorMessage = `OCPI-Admin API request failed with status ${response.status}`;
      try {
        const body = await response.clone().text();
        const json = JSON.parse(body);
        if (json.detail) errorMessage = cleanSetupErrorMessage(json.detail);
        else if (json.error) errorMessage = cleanSetupErrorMessage(json.error);
        else if (json.title) errorMessage = json.title;
      } catch {
        // not JSON, keep generic message
      }

      await ApiErrorMiddleware.handleErrorResponse(
        response,
        'OCPI-Admin API',
        endpoint
      );

      throw new Error(errorMessage);
    }
  }

  /**
   * Setup OCPI-to-VPOST connection for an external CPO operator
   * PUT /ocpi-admin/external-ocpi-cpo/{nayaxOperatorId}/connections/ocpi-to-vpost
   *
   * @param operatorId - Nayax operator ID
   * @param data - Connection configuration (versions_url, token_a, connection_version, csms_name)
   * @throws Error with user-friendly message for common error codes
   */
  async setupVpostConnection(
    operatorId: number,
    data: VpostConnectionRequestDto
  ): Promise<void> {
    const endpoint = `/external-ocpi-cpo/${operatorId}/connections/ocpi-to-vpost`;
    const headers = await this.tokenManager.getAuthHeaders();
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        accept: '*/*',
        ...headers,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      if (response.status === 409) {
        throw new Error(
          'An OCPI-to-VPOST connection already exists for this operator. ' +
          'Please contact support if you need to update the existing configuration.'
        );
      }

      if (response.status === 400) {
        throw new Error(
          'Invalid connection configuration. Please verify the Versions URL, Token, and CSMS name are correct.'
        );
      }

      // For other errors: try to extract 'detail' from the JSON body for a user-friendly message
      let vpostErrorMessage = `OCPI-Admin API request failed with status ${response.status}`;
      try {
        const body = await response.clone().text();
        const json = JSON.parse(body);
        if (json.detail) vpostErrorMessage = cleanSetupErrorMessage(json.detail);
        else if (json.error) vpostErrorMessage = cleanSetupErrorMessage(json.error);
        else if (json.title) vpostErrorMessage = json.title;
      } catch {
        // not JSON, keep generic message
      }

      await ApiErrorMiddleware.handleErrorResponse(
        response,
        'OCPI-Admin API',
        endpoint
      );

      throw new Error(vpostErrorMessage);
    }
  }

  /**
   * Get available CSMS names for OCPI-to-VPOST connection
   * GET /ocpi-admin/csms/names
   *
   * @returns Array of CSMS name strings
   */
  async getCsmsNames(): Promise<string[]> {
    const endpoint = `/csms/names`;
    return this.authenticatedRequest<string[]>(endpoint);
  }

  /**
   * Get available CSMS names for Kiosk connection setup
   * GET /ocpi-admin/csms
   *
   * @returns Array of CSMS name strings
   */
  async getKioskCsmsNames(): Promise<string[]> {
    const endpoint = `/csms`;
    return this.authenticatedRequest<string[]>(endpoint);
  }

  /**
   * Get all Nayax Kiosk connections
   * GET /ocpi-admin/connections/nayax-kiosk
   *
   * @returns Array of all kiosk connections
   */
  async getAllKioskConnections(): Promise<ProtocolConnection[]> {
    const endpoint = `/connections/nayax-kiosk`;
    return this.authenticatedRequest<ProtocolConnection[]>(endpoint);
  }

  /**
   * Update CPO party information for an internal CPO operator
   * PUT /ocpi-admin/internal-ocpi-cpo/{nayaxOperatorId}
   *
   * @param operatorId - Nayax operator ID
   * @param data - OCPI party information (ocpi_id, name)
   * @throws Error with user-friendly message for 409 Conflict (party ID already in use)
   */
  async updateCpoParty(operatorId: number, data: OcpiPartyDto): Promise<void> {
    const endpoint = `/internal-ocpi-cpo/${operatorId}`;
    const headers = await this.tokenManager.getAuthHeaders();
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        accept: '*/*',
        ...headers,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      // Handle 409 Conflict - Party ID already in use by another operator
      if (response.status === 409) {
        const partyId = `${data.ocpi_id.countryCode}-${data.ocpi_id.partyId}`;
        throw new Error(
          `The Party ID "${partyId}" is already in use by another operator. ` +
          `Please choose a different Party ID.`
        );
      }

      // For other errors, use the standard error handling
      await ApiErrorMiddleware.handleErrorResponse(
        response,
        'OCPI-Admin API',
        endpoint
      );

      // If handleErrorResponse doesn't throw, throw a generic error
      throw new Error(`OCPI-Admin API request failed with status ${response.status}`);
    }
  }

  /**
   * Publish locations to OCPI network (Internal CPO only)
   * POST /ocpi-admin/internal-ocpi-cpo/{nayaxOperatorId}/locations/publish
   *
   * @param operatorId - Nayax operator ID
   * @returns Publish result with success/failure details per machine
   */
  async publishLocations(operatorId: number): Promise<PublishLocationsResponse> {
    const endpoint = `/internal-ocpi-cpo/${operatorId}/locations/publish`;
    return this.authenticatedRequest<PublishLocationsResponse>(endpoint, {
      method: 'POST',
    });
  }

  /**
   * Publish tariffs to OCPI network (Internal CPO only)
   * POST /ocpi-admin/internal-ocpi-cpo/{nayaxOperatorId}/tariffs/publish
   *
   * @param operatorId - Nayax operator ID
   * @returns Publish result with success/failure details
   */
  async publishTariffs(operatorId: number): Promise<PublishTariffResponse> {
    const endpoint = `/internal-ocpi-cpo/${operatorId}/tariffs/publish`;
    return this.authenticatedRequest<PublishTariffResponse>(endpoint, {
      method: 'POST',
    });
  }

  /**
   * Sync latest CPO OCPI data for a specific connection and module (External CPO only)
   * POST /ocpi-admin/external-ocpi-cpo/{nayaxOperatorId}/connections/{connectionId}/synchronise/{ocpiModule}
   *
   * The backend retries incremental sync (max attempts from its appsettings), then falls back to a full sync.
   *
   * @param operatorId - Nayax operator ID
   * @param connectionId - Connection ID (party ID or country-partyId)
   * @param ocpiModule - OCPI module to sync: locations | tariffs | sessions | cdrs
   */
  async syncCpoOcpiData(
    operatorId: number,
    connectionId: number,
    ocpiModule: 'locations' | 'tariffs' | 'sessions' | 'cdrs'
  ): Promise<CpoSyncOutcome> {
    const endpoint = `/external-ocpi-cpo/${operatorId}/connections/${connectionId}/synchronise/${ocpiModule}`;
    const response = await this.authenticatedRequest<CpoSyncOutcomeResponse>(endpoint, { method: 'POST' });
    return {
      incrementalAttempts: response?.incremental_attempts ?? 1,
      fellBack: response?.fell_back ?? false,
    };
  }

  /**
   * Sync EVSEs (Electric Vehicle Supply Equipment) data
   * POST /ocpi-admin/internal-ocpi-cpo/{operatorId}/locations/evses/sync
   *
   * Synchronizes EVSE data for the specified operator.
   *
   * @param operatorId - Nayax operator ID
   * @param publishToOcpi - Whether to publish to OCPI after sync (default: true)
   * @returns Sync result (empty response on success)
   */
  async syncEvses(operatorId: number, publishToOcpi: boolean = true): Promise<void> {
    const params = new URLSearchParams({
      publishToOcpi: String(publishToOcpi),
    });
    const endpoint = `/internal-ocpi-cpo/${operatorId}/locations/evses/sync?${params.toString()}`;
    await this.authenticatedRequest<void>(endpoint, {
      method: 'POST',
    });
  }

  /**
   * Makes an authenticated request to the OCPI-Admin API
   * Follows the same pattern as other API clients in the project
   * @param endpoint - API endpoint path
   * @param options - Fetch options
   * @param suppressToastForStatus - Status codes to suppress toast for (e.g., [404])
   */
  private async authenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    suppressToastForStatus?: number[]
  ): Promise<T> {
    const headers = await this.tokenManager.getAuthHeaders();

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.baseUrl}${endpoint}`,
      {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          accept: '*/*',
          ...headers,
          ...options.headers,
        },
      },
      'OCPI-Admin API',
      endpoint,
      suppressToastForStatus
    );

    // Handle empty responses (204 No Content, etc.)
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return undefined as T;
    }

    const text = await response.text();
    if (!text) {
      return undefined as T;
    }

    return JSON.parse(text) as T;
  }

  /**
   * Validates that required tokens are available
   * Throws error if authentication is not available
   */
  private async validateAuthentication(): Promise<void> {
    const isValid = await this.tokenManager.isTokenValid();
    if (!isValid) {
      throw new Error('Authentication required. Please log in to access OCPI-Admin service.');
    }
  }

  /**
   * Health check method to test API connectivity
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.validateAuthentication();
      return true;
    } catch (error) {
      console.error('OCPI-Admin API connection test failed:', error);
      return false;
    }
  }

  /**
   * Makes an authenticated request with detailed error information
   * Captures full response body for error reporting to support
   * @param endpoint - API endpoint path
   * @param options - Fetch options
   * @param suppressToastForStatus - Status codes to suppress toast for (e.g., [404])
   */
  private async authenticatedRequestWithDetailedError<T>(
    endpoint: string,
    options: RequestInit = {},
    suppressToastForStatus?: number[]
  ): Promise<T> {
    const headers = await this.tokenManager.getAuthHeaders();
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        accept: '*/*',
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      // Capture full response for error reporting
      let responseBody = '';
      try {
        responseBody = await response.text();
      } catch {
        responseBody = '[Could not read response body]';
      }

      // Build detailed error message
      const errorDetails = [
        `Status: ${response.status} ${response.statusText}`,
        `URL: ${url}`,
        `Response Body:`,
        responseBody || '[Empty response]',
      ].join('\n');

      // Show toast for non-suppressed status codes
      const shouldSuppressToast = suppressToastForStatus?.includes(response.status);
      if (!shouldSuppressToast) {
        // Let the middleware show toast
        await ApiErrorMiddleware.handleErrorResponse(
          new Response(responseBody, { status: response.status, statusText: response.statusText }),
          'OCPI-Admin API',
          endpoint,
          suppressToastForStatus
        ).catch(() => {});
      }

      throw new Error(errorDetails);
    }

    // Handle empty responses (204 No Content, etc.)
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return undefined as T;
    }

    const text = await response.text();
    if (!text) {
      return undefined as T;
    }

    return JSON.parse(text) as T;
  }
}
